import { useEffect, useRef, useState } from 'react';
import { ButtonV2, DataGrid, DataGridColumn, StandardAlert, TooltipV2 } from '@cimplico/components';
import { CheckIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/20/solid';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { AccountClassification, EntityTaxRate, XeroReportTaxTypeEnum } from '../../../../../types/entity';
import { useIsActiveSubscription } from '../../../../../hooks/use-is-active-subscription';
import { ROW_SIZES } from '../../../../../constants';
import { toastNotification } from '../../../../../components/toast/Toast';
import { useUpdateEntityTaxRates } from '../../../../../queries/use-update-entity-tax-rates';
import { calcRelativeTime, formatDateTime } from '../../../../../utilities/formatting';
import { useRefreshXeroTaxRates } from '../../../../../queries/use-refresh-xero-tax-rates';
import SettingsContainer from '../../../../../components/settings-container/SettingsContainer';
import SectionLabel from '../../../../../components/section-label/SectionLabel';
import EmptyStateNew from '../../../../../components/empty-state/EmptyStateNew';
import EntityTaxRateRow from './EntityTaxRateRow';
import useWindowResize from '../../../../../hooks/use-window-resize';
import plural from '../../../../../utilities/plural';

const columns: DataGridColumn[] = [
    { alignment: 'center', width: '1fr' },
    { alignment: 'left', width: '0.1fr' },
];

const MD_SCREEN_SIZE = 768;

type TaxRateFields = {
    taxRates: {
        id: string;
        name: string;
        nameError?: string;
        accountClass: AccountClassification[];
        accountClassError?: string;
        rate?: number;
        xeroReportTaxType?: XeroReportTaxTypeEnum;
        xeroReportTaxTypeError?: string;
        sequence?: number;
        usedOnXero?: boolean;
        usedOnJournal?: boolean;
    }[];
};

type EntityTaxRatesProps = {
    entityId: string;
    isEditable: boolean;
    lastRefreshed?: Date;
    taxRates: EntityTaxRate[];
    connectedToXero: boolean;
    onXeroError: (error?: { name: string; message: string }) => void;
};

function EntityTaxRates({ entityId, isEditable, lastRefreshed, taxRates, connectedToXero, onXeroError }: EntityTaxRatesProps): JSX.Element {
    const [isEditing, setIsEditing] = useState(false);
    const [updateError, setUpdateError] = useState<{ [name: string]: string }[] | undefined>(undefined);
    const [createSuccessCount, setCreateSuccessCount] = useState<number>(0);

    const methods = useForm<TaxRateFields>({
        defaultValues: {
            taxRates:
                taxRates.length !== 0
                    ? taxRates.map((r) => {
                          return {
                              id: r.id,
                              name: r.name,
                              accountClass: r.type,
                              rate: r.rate,
                              xeroReportTaxType: r.xeroReportTaxType?.type || undefined,
                              sequence: r.sequence,
                              usedOnXero: Boolean(r.provider) && Boolean(r.providerId),
                              usedOnJournal: r.usedOnJournal,
                          };
                      })
                    : [{ id: 'row-0', name: '', accountClass: [] }],
        },
    });

    const activeSubscription = useIsActiveSubscription();
    const updateEntityRatesQuery = useUpdateEntityTaxRates(entityId);
    const refreshXeroTaxRatesQuery = useRefreshXeroTaxRates(entityId);

    const { width } = useWindowResize();
    const { append, remove } = useFieldArray({ control: methods.control, name: 'taxRates' });
    const rowIdCounter = useRef<number>(taxRates.length !== 0 ? taxRates.length - 1 : 1);

    function addRow(): void {
        append({ id: `row-${rowIdCounter.current + 1}`, name: '', accountClass: [] });
        rowIdCounter.current += 1;
    }

    function deleteRow(index: number): void {
        if (methods.getValues('taxRates').length === 1) {
            return;
        }
        remove(index);
    }

    async function handleSubmit(): Promise<void> {
        setUpdateError(undefined);

        const values = methods.getValues();

        const rowsWithValue = values.taxRates.filter(
            (r) => r.name !== '' || r.xeroReportTaxType || r.accountClass.length !== 0 || r.rate !== undefined
        );

        const invalidTaxRates = rowsWithValue.filter(
            (r) => r.accountClass.length === 0 || r.name === '' || (connectedToXero && !r.xeroReportTaxType)
        );

        if (invalidTaxRates.length > 0) {
            const ids = rowsWithValue.map((r) => r.id);

            methods.setValue(
                'taxRates',
                values.taxRates.map((r) => {
                    if (ids.includes(r.id) && r.accountClass.length === 0) {
                        return { ...r, accountClassError: 'An account class is required.' };
                    }
                    if (ids.includes(r.id) && !r.name) {
                        return { ...r, nameError: 'A name is required.' };
                    }
                    if (ids.includes(r.id) && !r.xeroReportTaxType) {
                        return { ...r, xeroReportTaxTypeError: 'A report tax type is required.' };
                    }
                    return r;
                })
            );

            return;
        }

        const hasDeletedRows = rowsWithValue.filter((rate) => !rate.id.includes('row-')).length < taxRates.length;

        // if no available entity tax rates
        if (rowsWithValue.length === 0 && !hasDeletedRows) {
            handleReset();
            return;
        }

        const allEntityTaxRates = rowsWithValue.map((r) => {
            return { ...r, rate: r.rate ? Number(r.rate) : 0, sequence: rowsWithValue.map((tr) => tr.id).indexOf(r.id) + 1 };
        });

        // if no changes on entity tax rates
        if (taxRates.length === allEntityTaxRates.length && allEntityTaxRates.every((val) => !val.id.includes('row-'))) {
            const defaultValues = taxRates
                .sort((a, b) => (a.sequence > b.sequence ? 1 : -1))
                .map((val) => {
                    return {
                        id: val.id,
                        name: val.name,
                        rate: val.rate,
                        type: val.type,
                        xeroReportTaxTypeEnum: val.xeroReportTaxType?.type,
                        sequence: val.sequence,
                    };
                });

            const newValues = allEntityTaxRates
                .sort((a, b) => (a.sequence > b.sequence ? 1 : -1))
                .map((val) => {
                    return {
                        id: val.id,
                        name: val.name,
                        rate: val.rate,
                        type: val.accountClass,
                        xeroReportTaxTypeEnum: val.xeroReportTaxType,
                        sequence: val.sequence,
                    };
                });

            if (JSON.stringify(defaultValues) === JSON.stringify(newValues)) {
                handleReset();
                return;
            }

            if (JSON.stringify(defaultValues) === JSON.stringify(newValues)) {
                handleReset();
            }
        }

        const allUpdatedRates = allEntityTaxRates.map((val) => {
            const matchingRate = taxRates.find((r) => r.id === val.id);

            if (matchingRate) {
                const updatedFields = {
                    name: matchingRate.name === val.name ? undefined : val.name,
                    type: JSON.stringify(matchingRate?.type) === JSON.stringify(val.accountClass) ? undefined : val.accountClass,
                    rate: matchingRate?.rate === val.rate ? undefined : val.rate,
                    xeroReportTaxType: matchingRate?.xeroReportTaxType?.type === val.xeroReportTaxType ? undefined : val.xeroReportTaxType,
                };
                return {
                    id: matchingRate.id,
                    fields: Object.values(updatedFields).every((field) => field === undefined) ? undefined : updatedFields,
                };
            }

            return { fields: { name: val.name, type: val.accountClass, rate: val.rate, xeroReportTaxType: val.xeroReportTaxType } };
        });

        updateEntityRatesQuery.mutate(allUpdatedRates, {
            onSuccess: () => {
                toastNotification('success', 'Entity tax rates updated!');
                setIsEditing(false);
            },
            onError: (err) => {
                const results = err.response?.data.results;
                setCreateSuccessCount(results.filter((res: { statusCode: number }) => res.statusCode === 200).length);
                setUpdateError(
                    results
                        .filter((res: { statusCode: number }) => res.statusCode === 400)
                        .map((res: { error: { fields: { [name: string]: string } } }) => res.error.fields)
                );
                toastNotification('error', 'Unable to update entity tax rates!');
            },
        });
    }

    function handleReset(): void {
        if (taxRates.length === 0) {
            methods.setValue('taxRates', [{ id: 'row-0', name: '', accountClass: [] }]);
        } else {
            methods.setValue(
                'taxRates',
                taxRates.map((r) => {
                    return {
                        id: r.id,
                        name: r.name,
                        accountClass: r.type,
                        rate: r.rate,
                        xeroReportTaxType: r.xeroReportTaxType?.type || undefined,
                        sequence: r.sequence,
                        usedOnXero: Boolean(r.provider) && Boolean(r.providerId),
                        usedOnJournal: r.usedOnJournal,
                    };
                })
            );
        }
        rowIdCounter.current = taxRates.length !== 0 ? taxRates.length - 1 : 1;
        setIsEditing(false);
        setUpdateError(undefined);
        setCreateSuccessCount(0);
    }

    function handleRefresh(): void {
        refreshXeroTaxRatesQuery.mutate('', {
            onSuccess: (res) => {
                toastNotification(
                    'success',
                    'Xero tax rates refreshed!',
                    `${res.created} created. ${res.updated} updated. ${res.deleted} deleted.`
                );
            },
            onError: (err) => {
                const error = err.response?.data.error;
                const message = err.response?.data.message;

                if (error && message) {
                    onXeroError({ message, name: error });
                }

                toastNotification('error', 'Unable to refresh xero tax rates!', message);
            },
        });
    }

    useEffect(() => {
        if (taxRates.length === 0) {
            methods.setValue('taxRates', [{ id: 'row-0', name: '', accountClass: [] }]);
        } else {
            methods.setValue(
                'taxRates',
                taxRates.map((r) => {
                    return {
                        id: r.id,
                        name: r.name,
                        accountClass: r.type,
                        rate: r.rate,
                        xeroReportTaxType: r.xeroReportTaxType?.type || undefined,
                        sequence: r.sequence,
                        usedOnXero: Boolean(r.provider) && Boolean(r.providerId),
                        usedOnJournal: r.usedOnJournal,
                    };
                })
            );
        }
    }, [taxRates]);

    function reorderLines(sortedIds: string[]): void {
        const prevValues = methods.getValues('taxRates');
        const sortedValues = [...prevValues].sort((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id));
        methods.setValue('taxRates', sortedValues);
    }

    function formatError(errors: { [name: string]: string }[]): string[] {
        const errorMessages: string[] = [];

        for (const err of errors) {
            errorMessages.push(`${Object.keys(err)[0]} - ${Object.values(err)[0]}`);
        }

        return errorMessages;
    }

    return (
        <FormProvider {...methods}>
            <SettingsContainer isLastItem>
                <SectionLabel
                    title="Tax Rates"
                    description="View and manage the entity tax rates to be used when creating journals, including tax rates from connected ledgers. Set custom tax rates that will be synced back to your connected ledger."
                />
                <section className="grid gap-y-8">
                    <div className="grid gap-y-2">
                        {createSuccessCount > 0 && (
                            <StandardAlert
                                title={`${createSuccessCount} entity tax ${plural(createSuccessCount, 'rate', 'rates')} updated!`}
                                type="success"
                            >
                                <></>
                            </StandardAlert>
                        )}
                        {updateError && (
                            <StandardAlert
                                title={`${updateError.length} entity tax ${plural(updateError.length, 'rate', 'rates')} failed to update!`}
                                description={formatError(updateError)}
                                type="error"
                            >
                                <></>
                            </StandardAlert>
                        )}
                        <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Tax Rates</label>
                            {connectedToXero && (
                                <div className="flex gap-2 items-center">
                                    <ButtonV2
                                        label={refreshXeroTaxRatesQuery.isLoading ? 'Refreshing' : 'Refresh'}
                                        size="xs"
                                        isDisabled={isEditing || refreshXeroTaxRatesQuery.isLoading}
                                        onClick={handleRefresh}
                                        isLoading={refreshXeroTaxRatesQuery.isLoading}
                                    />
                                    {lastRefreshed && (
                                        <TooltipV2 message={formatDateTime(lastRefreshed)} disabled={!lastRefreshed} position="right">
                                            <p className="text-gray-600 dark:text-gray-400 text-xs">{calcRelativeTime(lastRefreshed)}</p>
                                        </TooltipV2>
                                    )}
                                </div>
                            )}
                        </div>
                        <DataGrid
                            columns={columns}
                            rowSizes={ROW_SIZES}
                            totalRows={999}
                            isSelectable={false}
                            showRowBorder={width < MD_SCREEN_SIZE}
                            disablePagination
                            isDraggable
                            onMove={reorderLines}
                        >
                            {(isEditing || taxRates.length !== 0) &&
                                methods
                                    .getValues('taxRates')
                                    .map((rate, index: number) => (
                                        <EntityTaxRateRow
                                            id={rate.id}
                                            index={index}
                                            key={rate.id}
                                            onDelete={deleteRow}
                                            isEditing={isEditing}
                                            connectedToXero={connectedToXero}
                                            canRemove={methods.getValues('taxRates').length > 1}
                                        />
                                    ))}
                            {!isEditing && taxRates.length === 0 && (
                                <div className="w-full lg:w-[500px] pt-4 md:pt-0">
                                    <EmptyStateNew
                                        actionText="New Tax Rate"
                                        actionButtonType="secondary"
                                        helpText="Create new tax rates that will appear in addition to your firm defaults for journals."
                                        title="Add your first entity tax rate"
                                        titleSize="small"
                                        onClick={(): void => setIsEditing(true)}
                                        disableActionButton={!connectedToXero}
                                        disableActionButtonHint="Tax rates for a manual entity is coming soon!"
                                    />
                                </div>
                            )}
                        </DataGrid>
                        {isEditing && (
                            <div className="pl-14">
                                <ButtonV2 size="sm" type="secondary" label="Add rate" leftIcon={<PlusCircleIcon />} onClick={addRow} />
                            </div>
                        )}
                    </div>
                    {isEditing ? (
                        <div className="flex gap-x-4">
                            <ButtonV2 label="Save" size="sm" leftIcon={<CheckIcon />} onClick={handleSubmit} />
                            <ButtonV2 size="sm" type="hollow" label="Discard" leftIcon={<TrashIcon />} onClick={handleReset} />
                        </div>
                    ) : (
                        taxRates.length > 0 && (
                            <div>
                                <ButtonV2
                                    size="sm"
                                    type="secondary"
                                    label="Edit"
                                    leftIcon={<PencilSquareIcon />}
                                    onClick={(): void => setIsEditing(true)}
                                    isDisabled={!activeSubscription || !isEditable}
                                />
                            </div>
                        )
                    )}
                </section>
            </SettingsContainer>
        </FormProvider>
    );
}

export default EntityTaxRates;
